import React, {useEffect, useState} from 'react';
import {
    Box,
    Card,
    CardContent,
    Divider,
    FormControl,
    InputLabel,
    List,
    ListItem,
    MenuItem,
    Select,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import {ColumnType} from "../../../api/enum/ColumnType";
import {AnyColumnJson} from "../../../api/json/ColumnJson";
import {CsvColumnIntentJson} from "../../../api/json/CsvColumnIntentJson";
import styles from "./CsvImportColumnCard.module.scss";
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import {useProjectData} from "../../../hooks/useProjectData"

interface ImportColumnCardProps {
    intent: CsvColumnIntentJson
    sampleValues: string[]
    columns: AnyColumnJson[]
    error: boolean
    onChange: (newTypeOrField: string | null, name: string) => void
}

function CsvImportColumnCard({intent, sampleValues, columns, error, onChange}: ImportColumnCardProps) {
    const {project} = useProjectData()

    let initialMappingStrategy: "existing" | "new" | "skip" = intent.columnReference ? "existing" : "skip"
    if (initialMappingStrategy === "skip" && project && project.stats.totalRows === 0) {
        initialMappingStrategy = "new"
    }

    const [mappingStrategy, setMappingStrategy] = useState<"existing" | "new" | "skip">(initialMappingStrategy)
    const [existingFieldRef, setExistingFieldRef] = useState(intent.columnReference)
    const [newFieldType, setNewFieldType] = useState(intent.newField?.type ?? ColumnType.TEXT)
    const [newFieldName, setNewFieldName] = useState(intent.newField?.name ?? intent.columnHeader)

    useEffect(() => onChange(resolveNewTypeOrField(), newFieldName),
        [mappingStrategy, existingFieldRef, newFieldType, newFieldName],
    )

    const resolveNewTypeOrField = (): string | null => {
        switch (mappingStrategy) {
            case "new":
                return newFieldType
            case "existing":
                return existingFieldRef ?? null
            case "skip":
                return null
        }
    }

    return (
        <Stack spacing={{xs: 1, sm: 2, md: 4}}
               direction={{xs: 'column', md: 'row'}}
               justifyContent="flex-start"
               alignItems="center"
               divider={
                   <>
                       <ChevronRightIcon color="secondary" sx={{display: {md: "block", xs: "none"}}}/>
                       <KeyboardArrowDownIcon color="secondary" sx={{display: {md: "none", xs: "block"}}}/>
                   </>
               }
        >
            <Card variant="outlined" className={styles.cardContainer} sx={{width: 1}}>
                <CardContent>
                    <Typography variant="overline">Column</Typography>
                    <Typography variant="h2" component="div">{intent.columnHeader.substring(0, 60)}</Typography>
                    <List sx={{p: 0, mt: 1}}>
                        {sampleValues.map((sampleValue, index) =>
                            <Box key={index}>
                                <ListItem sx={{pl: 0, pr: 0}}>
                                    <Typography variant="body2"
                                                sx={{whiteSpace: "pre-wrap"}}>{sampleValue.substring(0, 255)}</Typography>
                                </ListItem>
                                {index < sampleValues.length - 1 && <Divider/>}
                            </Box>,
                        )}
                    </List>
                </CardContent>
            </Card>

            <Card variant="outlined" className={styles.cardContainer} sx={{width: 1}}>
                <CardContent>
                    <Typography variant="overline" gutterBottom>Column</Typography>
                    <ToggleButtonGroup
                        value={mappingStrategy}
                        exclusive
                        onChange={(_, newValue) => setMappingStrategy(newValue)}
                        fullWidth
                        aria-label="text alignment"
                        size="small"
                    >
                        {project && project.columns.length > 0 &&
                            <ToggleButton value="existing">Existing</ToggleButton>
                        }
                        <ToggleButton value="new">New</ToggleButton>
                        <ToggleButton value="skip">Skip</ToggleButton>
                    </ToggleButtonGroup>

                    {mappingStrategy === "existing" &&
                        <FormControl sx={{mt: 3, width: 1}}>
                            <InputLabel id="existing-column-select-label">Column</InputLabel>
                            <Select
                                labelId="existing-column-select-label"
                                label="Column"
                                value={existingFieldRef}
                                error={error}
                                onChange={(event) => setExistingFieldRef(event.target.value)}
                                fullWidth
                            >
                                {columns.map(column => (
                                    <MenuItem key={column.id} value={column.reference}>{column.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    }

                    {mappingStrategy === "new" &&
                        <Stack spacing={2} sx={{mt: 2}}>
                            <Typography variant="subtitle1">Define this column</Typography>
                            <TextField label="New Column Name"
                                       value={newFieldName}
                                       onChange={(event) => setNewFieldName(event.target.value)}
                            />
                            <FormControl>
                                <InputLabel id="column-type-select-label">Column Type</InputLabel>
                                <Select
                                    labelId="column-type-select-label"
                                    label="Column Type"
                                    value={newFieldType}
                                    error={error}
                                    onChange={(event) => setNewFieldType(event.target.value as ColumnType)}
                                    fullWidth
                                >
                                    {/*<MenuItem value={ColumnType.URL}>URL</MenuItem>*/}
                                    <MenuItem value={ColumnType.TEXT}>Text</MenuItem>
                                    <MenuItem value={ColumnType.NUMBER}>Number</MenuItem>
                                    <MenuItem value={ColumnType.PREDEFINED_OPTIONS}>Predefined Options</MenuItem>
                                </Select>
                            </FormControl>
                        </Stack>
                    }
                </CardContent>
            </Card>
        </Stack>

    )
}

export default CsvImportColumnCard;